import { useEffect, useState } from 'react';
import firebase, { db } from '../firebaseConfig';

// ---- Data model ------------------------------------------------------------
// The kit ships a neutral demo domain — rename `items` to whatever your app is
// about (trips, projects, albums, …). The PATTERNS are the point:
//
// items/{id}          : name, description, s3Prefix ('items/<id>/'), assetCount,
//                       totalBytes, createdAt
// items/{id}/assets   : name, sizeBytes, contentType, kind, s3Key, status,
//                       meta { status: 'queued'|'running'|'done'|'error' },
//                       lineage {…}, uploadedAt          ← written by uploads,
//                       processed by the backend meta worker (Firestore-as-queue)
// items/{id}/uploads  : live guest-upload progress rows (mirrored by the backend
//                       from drop-link heartbeats)
// dropLinks/{token}   : guest upload links (see createDropLink below)
// jobs/{id}           : fan-out job state + jobs/{id}/tasks/{taskId} telemetry
// jobControls/{jobId} : { action: 'cancel'|'pause'|'resume' } — kill switches
// config/app          : live-tunable backend settings (see shared/liveConfig.js)
// configs/app         : backend-written status (online flag, heartbeat)
// -----------------------------------------------------------------------------

const ready = () => firebase.apps.length > 0 && db;
const now = () => firebase.firestore.FieldValue.serverTimestamp();

export function useCollection(path, { orderBy, dir = 'desc' } = {}) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready()) {
      setDocs([]);
      return undefined;
    }
    let ref = db.collection(path);
    if (orderBy) ref = ref.orderBy(orderBy, dir);
    const unsub = ref.onSnapshot(
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        setError(err.message);
        setDocs([]);
      }
    );
    return () => unsub();
  }, [path, orderBy, dir]);

  return { docs, error };
}

export function useDoc(path) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready() || !path) return undefined;
    const unsub = db.doc(path).onSnapshot(
      (snap) => setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      (err) => setError(err.message)
    );
    return () => unsub();
  }, [path]);

  return { doc, error };
}

// ---- Items (the demo domain) ------------------------------------------------
export const useItems = () => useCollection('items', { orderBy: 'createdAt' });
export const useItem = (id) => useDoc(id ? `items/${id}` : null);

export function useItemAssets(itemId) {
  return useCollection(itemId ? `items/${itemId}/assets` : 'items', {
    orderBy: 'uploadedAt',
  });
}

// Live, in-flight uploads (mirrored by the backend from drop-link guests'
// progress heartbeats). Lets the owner watch a contributor's upload fill in
// before the finished file lands as a real asset.
export function useItemUploads(itemId) {
  return useCollection(itemId ? `items/${itemId}/uploads` : 'items', {
    orderBy: 'updatedMs',
  });
}

// Dismiss a stale/abandoned in-progress upload row from the owner's view.
export async function deleteItemUpload(itemId, id) {
  if (!ready() || !itemId || !id) return;
  await db.collection('items').doc(itemId).collection('uploads').doc(id).delete().catch(() => {});
}

export async function createItem({ name, description = '' }) {
  const ref = db.collection('items').doc();
  await ref.set({
    name: name || 'Untitled',
    description,
    s3Prefix: `items/${ref.id}/`,
    assetCount: 0,
    totalBytes: 0,
    createdAt: now(),
  });
  return ref.id;
}

export async function updateItem(itemId, patch) {
  await db.collection('items').doc(itemId).set(patch, { merge: true });
}

export async function deleteItem(itemId) {
  await db.collection('items').doc(itemId).delete();
}

// ---- Assets ------------------------------------------------------------------
// Records a finished OWNER upload as an asset (guests go through the backend's
// /drop/:token/record instead, which mirrors this shape via the admin SDK —
// keep the two in sync). Queues the backend meta worker via meta.status.
export async function recordAsset(itemId, asset) {
  const ref = db.collection('items').doc(itemId).collection('assets').doc();
  await ref.set({
    name: asset.name,
    sizeBytes: asset.sizeBytes || 0,
    contentType: asset.contentType || '',
    kind: asset.kind || 'file',
    s3Key: asset.s3Key,
    status: asset.status || 'stored',
    meta: { status: 'queued' },
    lineage: {
      origin: 'upload',
      by: firebase.auth().currentUser?.email || '',
      originalName: asset.name,
      addedAt: Date.now(),
      ...(asset.uploaderMeta ? { uploader: asset.uploaderMeta } : {}),
    },
    tags: Array.isArray(asset.tags) ? asset.tags.slice(0, 30) : [],
    ...(asset.folderPrefix ? { folderPrefix: asset.folderPrefix } : {}),
    ...(Array.isArray(asset.parts) ? { parts: asset.parts } : {}),
    uploadedAt: now(),
  });
  await db.collection('items').doc(itemId).set(
    {
      assetCount: firebase.firestore.FieldValue.increment(1),
      totalBytes: firebase.firestore.FieldValue.increment(asset.sizeBytes || 0),
    },
    { merge: true }
  );
  return ref.id;
}

export async function renameAsset(itemId, assetId, name) {
  await db.collection('items').doc(itemId).collection('assets').doc(assetId)
    .set({ name }, { merge: true });
}

export async function deleteAssetDoc(itemId, assetId) {
  await db.collection('items').doc(itemId).collection('assets').doc(assetId).delete();
}

// ---- Fan-out jobs -------------------------------------------------------------
// One job doc + a tasks subcollection with live telemetry per prep/chunk/stitch.
export function useJob(jobId) {
  const [job, setJob] = useState(undefined);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (!ready() || !jobId) {
      setJob(undefined);
      setTasks([]);
      return undefined;
    }
    const unsubJob = db.collection('jobs').doc(jobId).onSnapshot(
      (snap) => setJob(snap.exists ? { id: snap.id, ...snap.data() } : null),
      () => setJob(null)
    );
    const unsubTasks = db
      .collection('jobs')
      .doc(jobId)
      .collection('tasks')
      .onSnapshot(
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
          setTasks(items);
        },
        () => setTasks([])
      );
    return () => {
      unsubJob();
      unsubTasks();
    };
  }, [jobId]);

  return { job, tasks };
}

// All fan-out jobs (live + recent), newest first, for the Jobs page.
export function useJobs(max = 100, enabled = true) {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    if (!ready() || !enabled) {
      setJobs(null);
      return undefined;
    }
    // Rules let access admins list every job; everyone else lists their own
    // (jobs are org-scoped — see the /jobs rules). Try the wide query first
    // and fall back to "mine" when the rules refuse it.
    const onSnap = (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setJobs(items.slice(0, max));
    };
    let unsubMine = null;
    const unsub = db.collection('jobs').onSnapshot(onSnap, () => {
      const uid = firebase.auth().currentUser?.uid;
      if (!uid) { setJobs([]); return; }
      unsubMine = db.collection('jobs').where('requestedByUid', '==', uid).onSnapshot(onSnap, () => setJobs([]));
    });
    return () => { unsub(); if (unsubMine) unsubMine(); };
  }, [max, enabled]);
  return jobs;
}

const JOB_ACTIVE = ['queued', 'planning', 'running', 'stitching'];
export const jobIsActive = (job) => JOB_ACTIVE.includes(job?.status) || !!job?.paused;

// Queue a fan-out job for the backend orchestrator to adopt. `type` selects the
// role handlers in backend/runJob.js; `spec` is the job's input (kept small —
// big inputs belong in S3). Example: queueFanoutJob('manifest', { prefix:
// 'items/abc/' }).
export async function queueFanoutJob(type, spec, { title = '' } = {}) {
  const ref = db.collection('jobs').doc(`${type}-${Date.now()}`);
  await ref.set({
    kind: 'fanout',
    type,
    spec,
    title: title || null,
    status: 'queued',
    progress: 0,
    createdAt: Date.now(),
    requestedBy: firebase.auth().currentUser?.email || '',
    requestedByUid: firebase.auth().currentUser?.uid || null,
  });
  return ref.id;
}

// Write a control request the orchestrator watches. action: cancel|pause|resume.
// This is THE kill switch — every long-running backend job must honor it.
export async function controlJob(jobId, action) {
  if (!ready() || !jobId) return;
  await db.collection('jobControls').doc(jobId).set(
    { action, requestedAt: Date.now(), by: firebase.auth().currentUser?.email || 'admin' },
    { merge: true }
  );
}
export const cancelJob = (jobId) => controlJob(jobId, 'cancel');
export const pauseJob = (jobId) => controlJob(jobId, 'pause');
export const resumeJob = (jobId) => controlJob(jobId, 'resume');

// Delete a finished/errored job doc and its per-task telemetry, so the Jobs
// page can be tidied. (The orchestrator never touches done/error jobs, so
// removing them is safe.)
export async function deleteJob(jobId) {
  if (!ready() || !jobId) return;
  const jobRef = db.collection('jobs').doc(jobId);
  const tasks = await jobRef.collection('tasks').get();
  // Tasks subcollections are small (a few chunks + prep/stitch); one batch is
  // plenty, but chunk by 450 just in case.
  let batch = db.batch();
  let n = 0;
  for (const d of tasks.docs) {
    batch.delete(d.ref);
    n += 1;
    if (n % 450 === 0) {
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
      batch = db.batch();
    }
  }
  await batch.commit();
  await jobRef.delete();
}

// Bulk-delete every errored job. Returns the number removed.
export async function clearErroredJobs() {
  if (!ready()) return 0;
  const snap = await db.collection('jobs').where('status', '==', 'error').get();
  for (const d of snap.docs) {
    // eslint-disable-next-line no-await-in-loop
    await deleteJob(d.id);
  }
  return snap.size;
}

// ---- Guest drop links ----------------------------------------------------------
// dropLinks/{token} : itemId, itemName, label, ownerName, passcode (''|code),
//   status ('active'|'revoked'), createdAt, expiresAt (ms epoch),
//   uploadedBytes, uploadedCount, lastUploadAt, lastContributor.
// A contributor with the link uploads through the backend (token-auth), which
// records assets into the item via the admin SDK — guests never touch Firestore.
function randomToken(bytes = 18) {
  const arr = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function createDropLink(
  item,
  { label = '', passcode = '', expiresDays = 14, guestName = '', guestNote = '' } = {}
) {
  const token = randomToken();
  const me = firebase.auth().currentUser;
  const ownerName = me?.displayName || (me?.email ? me.email.split('@')[0] : '') || '';
  await db
    .collection('dropLinks')
    .doc(token)
    .set({
      token,
      itemId: item.id,
      itemName: item.name || '',
      label: label || '',
      ownerName,
      guestName: guestName || '',
      guestNote: guestNote || '',
      passcode: passcode || '',
      status: 'active',
      createdAt: now(),
      expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000,
      uploadedBytes: 0,
      uploadedCount: 0,
    });
  return token;
}

export function useDropLinks(itemId) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    if (!ready() || !itemId) {
      setLinks([]);
      return undefined;
    }
    const unsub = db
      .collection('dropLinks')
      .where('itemId', '==', itemId)
      .onSnapshot(
        (snap) => {
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setLinks(docs);
        },
        () => setLinks([])
      );
    return unsub;
  }, [itemId]);
  return links;
}

export async function revokeDropLink(token) {
  await db.collection('dropLinks').doc(token).set({ status: 'revoked' }, { merge: true });
}

export async function reactivateDropLink(token, { expiresDays = 14 } = {}) {
  await db
    .collection('dropLinks')
    .doc(token)
    .set({ status: 'active', expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000 }, { merge: true });
}

export async function deleteDropLink(token) {
  await db.collection('dropLinks').doc(token).delete();
}

// ---- Platform access invites -----------------------------------------------------
// accessInvites/{token} : email, note, landing, status ('pending'|'accepted'),
//   invitedBy, createdAt, acceptedAt — the token in the /welcome link is the
//   secret, same pattern as drop links.
// accessGrants/{email}  : the dynamic allowlist checked by the Firestore rules
//   (allowed() + platformUser()) — created together with the invite, so the
//   invitee is admitted the moment their account exists.
export async function createAccessInvite({ email, note = '', landing = '/orgs' }) {
  const token = randomToken();
  const em = String(email).trim().toLowerCase();
  const me = firebase.auth().currentUser;
  const batch = db.batch();
  batch.set(db.collection('accessInvites').doc(token), {
    email: em,
    note: note || '',
    landing,
    status: 'pending',
    invitedBy: me?.email || '',
    createdAt: Date.now(),
  });
  batch.set(db.collection('accessGrants').doc(em), {
    email: em,
    token,
    invitedBy: me?.email || '',
    createdAt: Date.now(),
  });
  await batch.commit();
  return token;
}

export const useAccessInvites = () =>
  useCollection('accessInvites', { orderBy: 'createdAt', dir: 'desc' });

// Revoking deletes both the invite and the grant — an already-created account
// keeps existing in Firebase Auth but loses all gated access (rules are the
// boundary).
export async function revokeAccessInvite(inv) {
  const batch = db.batch();
  batch.delete(db.collection('accessInvites').doc(inv.id));
  if (inv.email) batch.delete(db.collection('accessGrants').doc(inv.email));
  await batch.commit();
}

export async function getAccessInvite(token) {
  const snap = await db.collection('accessInvites').doc(token).get();
  return snap.exists ? { id: snap.id, ...snap.data() } : null;
}

export async function acceptAccessInvite(token) {
  await db
    .collection('accessInvites')
    .doc(token)
    .set({ status: 'accepted', acceptedAt: Date.now() }, { merge: true })
    .catch(() => {}); // best-effort bookkeeping — access comes from the grant
}

// ---- Outbound mail (mail queue → SES via api/mailWorker.js) -----------------------
// Clients queue { to, template, params }; the backend renders the template
// server-side and sends. Watch the returned doc for meta.status
// ('queued'→'running'→'done'|'error').
export async function queueMail({ to, template, params = {} }) {
  const ref = db.collection('mail').doc();
  await ref.set({
    to: String(to).trim().toLowerCase(),
    template,
    params,
    requestedBy: firebase.auth().currentUser?.email || '',
    createdAt: Date.now(),
    meta: { status: 'queued' },
  });
  return ref.id;
}

// ---- Live config (config/app) ---------------------------------------------------
// Client-editable runtime knobs the backend deep-merges over its defaults (see
// backend/shared/liveConfig.js). ANYTHING an operator may want to tune at
// runtime belongs there, never in a redeploy.
export const useLiveConfig = () => useDoc('config/app');
export async function saveLiveConfig(patch) {
  await db.collection('config').doc('app').set(patch, { merge: true });
}

// Backend-written status (online flag + heartbeat) for a health indicator.
export const useBackendStatus = () => useDoc('configs/app');
